import { Checkbox as ChakraCheckbox } from "@chakra-ui/react";
import type {
  ChangeEvent,
  ChangeEventHandler,
  ComponentProps,
  ReactNode,
} from "react";
import Checkmark from "../../icons/Checkmark/Checkmark.web";
import type { CheckboxSharedProps } from "./Checkbox.props";

type ChakraRootProps = ComponentProps<typeof ChakraCheckbox.Root>;
type CheckedState = ChakraRootProps["checked"];
type CheckedChangeDetails = { checked: CheckedState };

// Type augmentation for Chakra v3 checkbox slots that accept children
// TODO: Remove when @chakra-ui/react types are fixed
const Control = ChakraCheckbox.Control as React.FC<{ children?: ReactNode }>;
const Label = ChakraCheckbox.Label as React.FC<{ children?: ReactNode }>;

export type CheckboxProps = CheckboxSharedProps &
  Omit<
    ChakraRootProps,
    "checked" | "onCheckedChange" | "disabled" | "readOnly"
  > & {
    // v1/v2 props (backward compatibility)
    isIndeterminate?: boolean;
    isChecked?: boolean;
    isDisabled?: boolean;
    isReadOnly?: boolean;
    onChange?: ChangeEventHandler<HTMLInputElement>;

    // v3 props
    indeterminate?: boolean;
    checked?: CheckedState;
    disabled?: boolean;
    readOnly?: boolean;
  };

/**
 * Checkbox component with v1/v2 backward compatibility.
 *
 * Styling is handled by the checkbox slot recipe in theme.ts.
 */
export default function Checkbox({
  children,
  isIndeterminate,
  indeterminate,
  isChecked,
  checked,
  isDisabled,
  disabled,
  onChange,
  isReadOnly,
  readOnly,
  id,
  name,
  ...props
}: CheckboxProps): JSX.Element {
  // Map v1/v2 props to v3 props for backward compatibility
  const indeterminateValue =
    indeterminate !== undefined ? indeterminate : isIndeterminate;
  const checkedValue: CheckedState | undefined =
    checked !== undefined ? checked : isChecked;
  const disabledValue = disabled !== undefined ? disabled : isDisabled;
  const readOnlyValue = readOnly !== undefined ? readOnly : isReadOnly;

  return (
    <ChakraCheckbox.Root
      checked={indeterminateValue ? "indeterminate" : checkedValue}
      disabled={disabledValue}
      readOnly={readOnlyValue}
      id={id}
      name={name}
      ids={id ? { input: id } : undefined}
      onCheckedChange={(details: CheckedChangeDetails) => {
        if (!onChange) return;
        const eventLike = {
          target: {
            type: "checkbox",
            name: name ?? "",
            checked: details.checked === true,
          },
        };
        onChange(eventLike as unknown as ChangeEvent<HTMLInputElement>);
      }}
      {...props}
    >
      <ChakraCheckbox.HiddenInput />
      <Control>
        <ChakraCheckbox.Indicator>
          {!indeterminateValue && <Checkmark primary="fg.inverted" />}
        </ChakraCheckbox.Indicator>
      </Control>
      {children != null && <Label>{children}</Label>}
    </ChakraCheckbox.Root>
  );
}
